import {
  Guid,
  LineItemCategory,
  PaymentInfo,
  PaymentInfoStatus,
  Quote,
  QuotePaymentStatus,
  SalesItemCode,
  SalesItemSubType,
  SalesItemType,
  Segment,
  SegmentType,
} from "../Types";
import { DestinationContent, QuoteData } from "../../contexts/QuoteContext/QuoteContext";
import { PriceSegmentDto } from "../../apis/generatedProductCatalogApi";

export type QuoteInformation = {
  quoteInfo: Quote;
  courseSegments: Segment[];
  startAt: string;
  endAt: string;
  isMultiQuote: boolean;
  courseInfo: string | undefined;
  totalDuration: number;
  hasFlightArticle: boolean;
};

const getPaymentStatus = (oppHasPaidQuote: boolean, payments?: Array<PaymentInfo>): QuotePaymentStatus => {
  if (payments?.some((payment) => payment.status === PaymentInfoStatus.Paid)) return QuotePaymentStatus.Paid;
  if (payments?.some((payment) => payment.status === PaymentInfoStatus.Reserve)) return QuotePaymentStatus.Reserved;
  if (oppHasPaidQuote) return QuotePaymentStatus.Canceled;
  return QuotePaymentStatus.Unreserved;
};

const getQuoteWithBundleItemsBundled = (rawQuote: Quote): Quote => {
  if (!rawQuote.segments) {
    return rawQuote;
  }
  // make a copy to not modify the object passed as an argument
  const quote: Quote = JSON.parse(JSON.stringify(rawQuote));
  quote.segments.forEach((segment) => {
    if (!segment.lineItems) {
      return;
    }
    const bundledLineItems = segment.lineItems.filter((lineItem) => lineItem.isBundled);
    const bundleParentIds = new Set<Guid>();
    bundledLineItems.forEach((element) => {
      bundleParentIds.add(element.bundleId);
    });

    bundleParentIds.forEach((bundleId) => {
      const bundleItems = bundledLineItems.filter((item) => item.bundleId === bundleId);
      const parentItem = bundledLineItems.find((item) => item.bundleId === item.id)!;
      if (parentItem === undefined) {
        console.error(
          `Quote with id ${rawQuote.id} contains bundle items but the parent for the bundle item with id ${bundleId} cannot be found!`
        );
      }

      bundleItems.forEach((item) => {
        if (item.id !== parentItem.id) {
          parentItem.totalPrice.amount += item.totalPrice.amount;
          if (parentItem.category === LineItemCategory.Course) {
            segment.course.price.amount += item.totalPrice.amount;
            segment.lineItems = segment.lineItems.filter((lineItem) => lineItem.id !== item.id);
          }
        }
      });
    });
  });
  return quote;
};

export const calculateTotalPrice = (q: Quote, discount?: number) => {
  const discountAmount = discount !== undefined && Math.abs(discount) > 0 ? Math.abs(discount) : 0;
  return discountAmount > 0 ? q.prices?.totalPrice.amount - discountAmount : q.prices?.totalPrice.amount;
};

export function getPaidInFullDiscount(prices: PriceSegmentDto[], paymentInFullAllowed?: boolean): number {
  if (prices.length > 0 && paymentInFullAllowed === true) {
    return prices.filter((x) => x.isDeleted === false).at(0)?.value ?? 0;
  }
  return 0;
}

export function getPaidQuote(quotes: Array<QuoteData>): QuoteData | null {
  return quotes.find((x: QuoteData) => x.quoteData.status === QuotePaymentStatus.Paid) || null;
}

//Remove all the bundled lineItems and add their price to the price of the course
export function processBundleItemsAndPaymentStatus(rawQuote: Quote, oppHasPaidQuote: boolean): Quote {
  const quote = getQuoteWithBundleItemsBundled(rawQuote);
  quote.status = getPaymentStatus(oppHasPaidQuote, quote.payments);
  return quote;
}

export function getQuoteInformation(quote: Quote): QuoteInformation {
  const courseSegments = quote.segments?.filter((segment) => segment.type === SegmentType.Course) ?? [];
  const startAt = quote.segments[0]?.startAt || ""; //The component where this is used won't render if quoteData is undefined
  const endAt = quote.segments[quote.segments?.length - 1]?.endAt || ""; // so we can have a default of "" to avoid type errors
  const isMultiQuote = (quote.segments?.length || 0) > 1;
  const courseInfo = isMultiQuote
    ? ""
    : courseSegments?.[0]?.lineItems.find((lineItem) => lineItem.type == SalesItemType.Course)?.description;
  const totalDuration = courseSegments ? courseSegments.reduce((acc, val) => acc + val.duration.value, 0) : 0;

  // Check if booking includes an FTEFX or FTEF flight article
  const hasFlightArticle =
    quote.segments?.some((segment) =>
      segment.lineItems?.some(
        (lineItem) =>
          lineItem.subType === SalesItemSubType.Flight &&
          (lineItem.code === SalesItemCode.FTEFX || lineItem.code === SalesItemCode.FTEF)
      )
    ) ?? false;

  return {
    quoteInfo: quote,
    courseSegments,
    startAt,
    endAt,
    isMultiQuote,
    courseInfo,
    totalDuration,
    hasFlightArticle,
  };
}

export function getDestinationName(
  segments: Segment[],
  destinations: Map<string, DestinationContent>,
  separator: string = " - "
): string {
  if (!segments || !destinations) return "";

  return segments
    .map((segment) => destinations.get(segment.destinationCode)?.name ?? segment.destinationCode)
    .join(separator);
}
